import { supabase } from '@/lib/supabase'
import type { Customer, ListCustomersParams, ListCustomersResult, CustomerStats } from '../types'
import { formatPlanLabel } from '@/modules/plans/utils/formatPlanLabel'
import { subStatusToCustomerStatus } from '../utils/status'

// Rank de status (maior = melhor)
function rankStatus(st: string | null | undefined) {
  const raw = String(st || '').toLowerCase()
  return raw === 'active' ? 3 : raw === 'trialing' ? 2 : raw === 'past_due' ? 1 : 0
}

// Universo base: todas as contas (sem excluir por VIP/role)
async function getAllAccountIds(search: string): Promise<string[]> {
  let accAllQ: any = supabase.from('accounts').select('id, created_at')
  if (search) { const s = `%${search}%`; accAllQ = accAllQ.ilike('name', s) }
  accAllQ = accAllQ.order('created_at', { ascending: false })
  const { data: accRows } = await accAllQ
  const accAll = (accRows || []) as Array<{ id: string }>
  return accAll.map(a => a.id)
}

export async function listCustomerIds({ search = '' }: { search?: string }): Promise<string[]> {
  try { return await getAllAccountIds(search) } catch { return [] }
}

export async function listCustomers({ search = '', page = 1, pageSize = 25, status = '', planId = '' }: ListCustomersParams): Promise<ListCustomersResult> {
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1
  try {
    const accAllIds = await getAllAccountIds(search)
    if (!accAllIds.length) {
      const empty: CustomerStats = { total: 0, ativo: 0, pendente: 0, suspenso: 0, inativo: 0 }
      return { items: [], total: 0, stats: empty }
    }

    // Melhor assinatura por conta (universo)
    const { data: subsAll } = await supabase
      .from('subscriptions')
      .select('account_id, status, plan_id, current_period_end, created_at')
      .in('account_id', accAllIds)
    const bestByAccAll = new Map<string, any>()
    for (const s of (subsAll || [])) {
      const prev = bestByAccAll.get(s.account_id)
      if (!prev) bestByAccAll.set(s.account_id, s)
      else {
        const better = rankStatus(s.status) > rankStatus(prev.status) || (rankStatus(s.status) === rankStatus(prev.status) && ((s.current_period_end || s.created_at || '') > (prev.current_period_end || prev.created_at || '')))
        if (better) bestByAccAll.set(s.account_id, s)
      }
    }

    // Filtros antes da paginação
    let filteredIds = accAllIds.filter(id => {
      const b = bestByAccAll.get(id)
      const st = subStatusToCustomerStatus(b?.status)
      const planOk = planId ? ((b?.plan_id || '') === planId) : true
      const statusOk = status ? (st === status) : true
      return planOk && statusOk
    })

    // Ordenar por recência da melhor assinatura; fallback para created_at
    const { data: accountsOrder } = await supabase.from('accounts').select('id, created_at').in('id', filteredIds)
    const createdAtById = new Map<string, string>((accountsOrder || []).map((a: any) => [a.id, a.created_at || '']))
    filteredIds.sort((a, b) => {
      const A = bestByAccAll.get(a), B = bestByAccAll.get(b)
      const ad = (A?.current_period_end || A?.created_at || createdAtById.get(a) || '') as string
      const bd = (B?.current_period_end || B?.created_at || createdAtById.get(b) || '') as string
      return bd.localeCompare(ad)
    })

    const pagedIds = filteredIds.slice(from, to + 1)

    // Accounts da página
    const { data: accounts } = await supabase.from('accounts').select('id, name, created_at, primary_user_id').in('id', pagedIds)
    const ids = (accounts || []).map((a: any) => a.id)

    // Owners + perfis para a página
    const { data: ownersPage } = ids.length ? await supabase.from('account_users').select('account_id, user_id, role').in('account_id', ids).eq('role', 'owner') : { data: [] as any[] }
    const ownerByAccPage = new Map<string, string>()
    for (const a of (accounts || [])) {
      const ow = (ownersPage || []).find((o: any) => o.account_id === a.id)
      ownerByAccPage.set(a.id, ow?.user_id || (a as any)?.primary_user_id || null)
    }
    const ownerUserIdsPage = Array.from(new Set(Array.from(ownerByAccPage.values()).filter(Boolean))) as string[]
    const { data: profsPage } = ownerUserIdsPage.length ? await supabase.from('v_profiles_contact_master').select('id, full_name, email').in('id', ownerUserIdsPage) : { data: [] as any[] }
    const profileById = new Map((profsPage || []).map((p: any) => [p.id, p]))

    // Melhor assinatura (página): reutiliza bestByAccAll
    const bestByAcc = new Map<string, any>()
    for (const id of ids) { const b = bestByAccAll.get(id); if (b) bestByAcc.set(id, b) }

    // Labels de plano
    const planIds = Array.from(new Set(ids.map(id => bestByAcc.get(id)?.plan_id).filter(Boolean))) as string[]
    const { data: planRows } = planIds.length ? await supabase.from('plans').select('id, name, features').in('id', planIds) : { data: [] as any[] }
    const planNameById = new Map<string, string>()
    for (const p of (planRows || [])) {
      const fx: any = p?.features || {}
      planNameById.set(p.id, formatPlanLabel({ name: p.name, amount_cents: Number(fx.amount_cents || 0), currency: String(fx.currency || 'BRL'), interval: String(fx.interval || 'month') as any }))
    }

    const accountById = new Map((accounts || []).map((a: any) => [a.id, a]))
    const items: Customer[] = pagedIds.map((id) => {
      const a: any = accountById.get(id)
      const ownerId = ownerByAccPage.get(id)
      const prof: any = ownerId ? profileById.get(ownerId) : null
      const bs = bestByAcc.get(id)
      const planName = bs?.plan_id ? (planNameById.get(bs.plan_id) || null) : null
      // Fallback de e-mail: se o profile não trouxe e o nome da conta parece um email
      const emailFallback = (() => { try { const nm = String(a?.name || ''); return nm.includes('@') ? nm : null } catch { return null } })()
      const email = prof?.email || emailFallback || null
      // Fallback de nome: se o nome da conta for e-mail, manter nome como parte local
      const name = (() => { if (a?.name) { const nm = String(a.name); if (nm.includes('@')) { try { return nm.split('@')[0] } catch { return nm } } return nm } return prof?.full_name || null })()
      return { id, name, email, created_at: a?.created_at ?? null, status: subStatusToCustomerStatus(bs?.status), current_plan_name: planName, current_plan_id: bs?.plan_id ?? null }
    }).filter(Boolean)

    // Stats (universo completo, sem excluir VIP/roles)
    let stats: CustomerStats = { total: 0, ativo: 0, pendente: 0, suspenso: 0, inativo: 0 }
    for (const accId of accAllIds) {
      const bs = bestByAccAll.get(accId)
      const st = subStatusToCustomerStatus(bs?.status)
      if (st === 'ativo') stats.ativo++
      else if (st === 'suspenso') stats.suspenso++
      else if (st === 'inativo') stats.inativo++
      else stats.pendente++
    }
    stats.total = stats.ativo + stats.pendente + stats.inativo
    return { items, total: filteredIds.length, stats }
  } catch {}
  const empty: CustomerStats = { total: 0, ativo: 0, pendente: 0, suspenso: 0, inativo: 0 }
  return { items: [], total: 0, stats: empty }
}

export async function inactivateCustomer(accountId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('admin-inactivate-customer', { body: { accountId } })
  if (error) throw error
}

export async function activateCustomer(accountId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('admin-activate-customer', { body: { accountId } })
  if (error) throw error
}

export async function deleteCustomer(accountId: string): Promise<void> {
  // Finaliza imediatamente assinaturas (status 'canceled') antes de remover
  try { await supabase.functions.invoke('admin-inactivate-customer', { body: { accountId } }) } catch {}
  // Usa sempre a Edge Function para garantir limpeza completa (inclui comissões pendentes de afiliado)
  const { error } = await supabase.functions.invoke('admin-delete-customer', { body: { accountId } })
  if (error) throw error
}

export async function deleteCustomersBulk(accountIds: string[]): Promise<void> {
  if (!accountIds?.length) return
  for (const id of accountIds) await deleteCustomer(id)
}

export async function updateCustomerName(accountId: string, name: string): Promise<void> {
  const { error } = await supabase.from('accounts').update({ name }).eq('id', accountId)
  if (error) throw error
}

export async function adminChangeCustomerPlan(
  accountId: string,
  planId: string,
): Promise<{ mode: 'upgrade' | 'downgrade' | 'already_on_plan'; scheduled_for?: string | null; subscription_id?: string | null }> {
  const { data, error } = await (supabase.functions as any).invoke('admin-change-subscription-plan', { body: { accountId, plan: planId, confirm: true } })
  if (error) throw error
  const payload = data as any
  if (payload?.error) throw new Error(String(payload.error))
  if (String(payload?.message || '').toLowerCase() === 'already_on_plan') {
    return {
      mode: 'already_on_plan',
      scheduled_for: null,
      subscription_id: payload?.subscription_id || null,
    }
  }
  const rawMode = String(payload?.mode || '').toLowerCase()
  if (rawMode !== 'upgrade' && rawMode !== 'downgrade') {
    const message = String(payload?.message || 'Cliente sem assinatura ativa em Stripe para troca de plano')
    throw new Error(message)
  }
  const mode = (rawMode === 'upgrade' ? 'upgrade' : 'downgrade') as 'upgrade' | 'downgrade'
  const scheduled_for = payload?.scheduled_for || null
  return { mode, scheduled_for, subscription_id: payload?.subscription_id || null }
}

export async function ensureCustomer(email: string, name?: string, phone?: string): Promise<{ accountId: string; userId: string }>{
  const payload = { email, name, phone }
  const { data, error } = await supabase.functions.invoke('admin-ensure-customer', { body: payload as any })
  if (error) throw error
  const out = (Array.isArray(data) ? data[0] : data) as any
  if (out?.error) throw new Error(String(out.error))
  return { accountId: out?.accountId, userId: out?.userId }
}

// Sincroniza assinatura mais recente; se id local estiver inválido, tenta reparar e repetir
export async function syncAccountLatestSubscription(accountId: string): Promise<void> {
  const { data: subs } = await supabase.from('subscriptions').select('stripe_subscription_id').eq('account_id', accountId).order('created_at', { ascending: false }).limit(1)
  const subId = subs?.[0]?.stripe_subscription_id as string | undefined
  if (!subId) throw new Error('Conta sem assinatura para sincronizar')
  const invokeSync = async () => (supabase.functions as any).invoke('sync-subscription-status', { body: { subscription_id: subId } })
  const res: any = await invokeSync()
  if (!res?.error) return
  const ctx: Response | undefined = res?.response || (res?.error as any)?.context
  let msg: string | null = null
  try {
    if (ctx) {
      const ct = ctx.headers.get('content-type') || ''
      if (ct.includes('application/json')) { const j = await ctx.json().catch(()=> null as any); msg = (j?.error || j?.message || null) as string | null }
      else { const t = await ctx.text().catch(()=> ''); msg = t || null }
    }
  } catch {}
  if (String(msg || '').toLowerCase().includes('no such subscription')) {
    try { await (supabase.functions as any).invoke('admin-repair-subscription', { body: { accountId } }) } catch {}
    const retry: any = await invokeSync()
    if (retry?.error) throw new Error('Falha ao sincronizar após reparo')
    return
  }
  if (msg) throw new Error(String(msg))
  throw res?.error
}
